import { useCallback, useEffect, useMemo, useState } from 'react';

export const OPINION_TYPES = Object.freeze([
  Object.freeze({ id: 'against', min: 1, max: 3, label: 'opinionTypeAgainst' }),
  Object.freeze({ id: 'neutral', min: 4, max: 4, label: 'opinionTypeNeutral' }),
  Object.freeze({ id: 'for', min: 5, max: 7, label: 'opinionTypeFor' }),
]);

const allTypes = Object.freeze(OPINION_TYPES.map(({ id }) => id));
const emptyList = Object.freeze([]);

function opinionScore(value) {
  if (typeof value === 'string' && /^[1-7]$/.test(value)) return Number(value);
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 7 ? value : null;
}

// The server already enforces event, audience, search and privacy. This only
// narrows its complete public list, preserving the received order and records.
export default function useOpinionTypes(reactions, contextKey) {
  const [selection, setSelection] = useState(() => ({ contextKey, types: allTypes }));
  // Never show the previous event/account's selection, even before effects run.
  const types = selection.contextKey === contextKey ? selection.types : allTypes;

  useEffect(() => {
    // Persist the reset too: returning to an earlier key must not resurrect it.
    setSelection(previous => previous.contextKey === contextKey ? previous : { contextKey, types: allTypes });
  }, [contextKey]);

  const toggleType = useCallback(id => {
    if (!allTypes.includes(id)) return;
    setSelection(previous => {
      const current = previous.contextKey === contextKey ? previous.types : allTypes;
      const selected = current.includes(id);
      if (selected && current.length === 1) return previous.contextKey === contextKey ? previous : { contextKey, types: current };
      return { contextKey, types: allTypes.filter(type => type === id ? !selected : current.includes(type)) };
    });
  }, [contextKey]);

  const visibleReactions = useMemo(() => {
    const source = Array.isArray(reactions) ? reactions : emptyList;
    return source.filter(reaction => {
      const score = opinionScore(reaction?.scope);
      return score !== null && OPINION_TYPES.some(type => types.includes(type.id) && score >= type.min && score <= type.max);
    });
  }, [reactions, types]);

  return { types, visibleReactions, toggleType };
}
